import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ProjectContextResponse } from '@ai-meeting/shared'
import { ApiError } from '@/services/api'
import {
  fetchProjectContextVersion,
  fetchProjectContextVersions,
  restoreProjectContextVersion,
} from '@/services/projects'
import { useUiStrings } from '@/hooks/useUiStrings'
import {
  mapVersionDetail,
  mapVersionListItem,
  type ProjectContextVersion,
} from './projectContextUi'

export type ProjectContextVersionsLoadState = 'loading' | 'ready' | 'error'

/**
 * Loads context version history for the Context & Dictionary tab.
 * Restore refreshes the list and returns the updated context for the parent.
 */
export function useProjectContextVersions(projectKey: string) {
  const { t } = useUiStrings()
  const labels = useMemo(
    () => ({
      human: t.projectDetailVersionSourceHuman,
      manual: t.projectDetailVersionSourceManual,
      restore: t.projectDetailVersionSourceRestore,
      unknownMeeting: t.projectDetailVersionUnknownMeeting,
    }),
    [
      t.projectDetailVersionSourceHuman,
      t.projectDetailVersionSourceManual,
      t.projectDetailVersionSourceRestore,
      t.projectDetailVersionUnknownMeeting,
    ]
  )
  const [state, setState] = useState<ProjectContextVersionsLoadState>('loading')
  const [versions, setVersions] = useState<ProjectContextVersion[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    if (!projectKey.trim()) {
      setState('error')
      setErrorMessage(t.failedLoadProjectContextVersions)
      setVersions([])
      return
    }

    setState('loading')
    setErrorMessage(null)
    try {
      const data = await fetchProjectContextVersions(projectKey)
      setVersions(data.versions.map((item) => mapVersionListItem(item, labels)))
      setState('ready')
    } catch (err: unknown) {
      setVersions([])
      setState('error')
      setErrorMessage(
        err instanceof ApiError ? err.message : t.failedLoadProjectContextVersions
      )
    }
  }, [projectKey, labels, t.failedLoadProjectContextVersions])

  useEffect(() => {
    let active = true
    setState('loading')
    setErrorMessage(null)

    if (!projectKey.trim()) {
      setState('error')
      setErrorMessage(t.failedLoadProjectContextVersions)
      setVersions([])
      return
    }

    void fetchProjectContextVersions(projectKey)
      .then((data) => {
        if (!active) return
        setVersions(data.versions.map((item) => mapVersionListItem(item, labels)))
        setState('ready')
      })
      .catch((err: unknown) => {
        if (!active) return
        setVersions([])
        setState('error')
        setErrorMessage(
          err instanceof ApiError ? err.message : t.failedLoadProjectContextVersions
        )
      })

    return () => {
      active = false
    }
  }, [projectKey, labels, t.failedLoadProjectContextVersions])

  const loadDetail = useCallback(
    async (version: ProjectContextVersion): Promise<ProjectContextVersion> => {
      const detail = await fetchProjectContextVersion(projectKey, version.id)
      const mapped = mapVersionDetail(detail, labels)
      // Preserve list `active` — detail response does not carry the pointer.
      return { ...mapped, active: version.active }
    },
    [projectKey, labels]
  )

  const restore = useCallback(
    async (version: ProjectContextVersion): Promise<ProjectContextResponse> => {
      setBusy(true)
      setErrorMessage(null)
      try {
        const context = await restoreProjectContextVersion(projectKey, version.id)
        await reload()
        return context
      } catch (err: unknown) {
        const message =
          err instanceof ApiError ? err.message : t.failedRestoreProjectContextVersion
        setErrorMessage(message)
        throw err
      } finally {
        setBusy(false)
      }
    },
    [projectKey, reload, t.failedRestoreProjectContextVersion]
  )

  return {
    state,
    versions,
    errorMessage,
    busy,
    reload,
    loadDetail,
    restore,
  }
}
